import { Option, Select } from '@shared/components/react-hook-form';
import {
  AvailabilityCheckFields,
  OpendockDock,
  OpendockLoadType,
  OpendockWarehouse,
} from '@shared/generated/graphql';
import {
  OpendockAvailabilityInputFields,
  OpendockScheduleAppointmentInputFields,
  ScheduleStop,
} from 'clerk_common/types';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { DateOption, generateDateOptions } from '../util';

const NUM_DATE_OPTIONS = 7;

type Props = {
  fields: AvailabilityCheckFields;
  stop: ScheduleStop;
  noTimes: boolean;
  handleFieldsChange: (i: OpendockAvailabilityInputFields) => Promise<void>;
  setInputFields: (i: OpendockAvailabilityInputFields) => void;
};

type OpendockFormValues = {
  day?: DateOption;
  warehouse: OpendockWarehouse;
  loadType: OpendockLoadType;
  dock: OpendockDock;
};

export function OpendockFields({
  fields,
  handleFieldsChange,
  setInputFields,
  stop,
  noTimes,
}: Props) {
  const { control, watch } = useForm<OpendockFormValues>();

  async function handleChange() {
    const formValues = watch();
    const inputFields = toInputFields(formValues);
    setInputFields(inputFields);
    await handleFieldsChange(inputFields);
  }

  // NOTE(jonathan): useEffect here is an unusual pattern of a child component
  // setting default (initial) state owned by a parent. There is probably a better/
  // cleaner way.
  useEffect(() => {
    const inputFields: OpendockScheduleAppointmentInputFields = {
      day: stop.day ?? '',
      warehouseId: fields.warehouse.id,
      loadTypeId: fields.loadType.id,
      dockId: fields.dock.id,
    };
    setInputFields(inputFields);
  }, []);

  // NOTE(jonathan): We use find() because <Select> needs reference equality.
  const warehouse = find(fields.warehouses, fields.warehouse);
  const loadType = find(fields.loadTypes, fields.loadType);
  const dock = find(fields.docks, fields.dock);

  if (fields.warehouses.length == 0) return null;
  if (fields.loadTypes.length == 0) return null;
  if (fields.docks.length == 0) return null;

  const dateOptions = generateDateOptions(stop.day, NUM_DATE_OPTIONS);
  const defaultDate = dateOptions.find((o) => o.value == fields.day);

  return (
    <div className="mt-3">
      {noTimes && (
        <Select
          label="Date"
          name="day"
          defaultValue={defaultDate}
          renderValue={(o: DateOption) => o.label}
          className="mb-3"
          control={control}
          onChange={handleChange}
        >
          {dateOptions.map((o) => (
            <Option value={o} key={o.value}>
              {o.label}
            </Option>
          ))}
        </Select>
      )}
      <Select
        name="warehouse"
        label="Warehouse"
        defaultValue={warehouse}
        renderValue={renderValue}
        className="mb-3"
        control={control}
        onChange={handleChange}
      >
        {fields.warehouses.map((o) => (
          <Option value={o} key={o.id}>
            {o.name}
          </Option>
        ))}
      </Select>
      <Select
        name="loadType"
        label="Load Type"
        defaultValue={loadType}
        renderValue={renderValue}
        className="mb-3"
        control={control}
        onChange={handleChange}
      >
        {fields.loadTypes.map((o) => (
          <Option value={o} key={o.id}>
            {o.name}
          </Option>
        ))}
      </Select>
      <Select
        name="dock"
        label="Dock"
        defaultValue={dock}
        renderValue={renderValue}
        className="mb-3"
        control={control}
        onChange={handleChange}
      >
        {fields.docks.map((o) => (
          <Option value={o} key={o.id}>
            {o.name}
          </Option>
        ))}
      </Select>
    </div>
  );
}

function renderValue(v: OpendockWarehouse | OpendockLoadType | OpendockDock) {
  return v.name;
}

function find<T extends { id: string }>(arr: T[], elem: T): T {
  const result = arr.find((i) => i.id == elem.id);
  // NOTE(jonathan): Fail silently.
  if (!result) return arr[0];
  return result;
}

function toInputFields(
  formValues: OpendockFormValues
): OpendockScheduleAppointmentInputFields {
  return {
    day: formValues.day?.value,
    warehouseId: formValues.warehouse.id,
    loadTypeId: formValues.loadType.id,
    dockId: formValues.dock.id,
  };
}
